// @ts-nocheck
import * as React from "react";
import { Ic, ElderTabBar, AvatarImg, useCurrentUser, supportPhone } from "./common";

// 어르신 본인(citizen) 1인칭 전용 홈. 기존 보호자 홈(dashboard-guardian)이 전부
// 3인칭("오늘 어머니는…")이라, 어르신이 직접 로그인하면 자기를 "어머니"로 부르는
// 화면을 보게 되는 문제를 해결한다. 데이터는 보호자 홈과 동일한
// GET /api/v1/dashboard/guardian 를 재사용하고(citizen 로그인 시 resolve_elderly가
// 본인 Elderly 반환), 관점(copy)·정보구조·접근성(큰 글씨/큰 버튼)만 바꾼다.
// 설계: docs/elder-home-design.md

const CONSULT_TEL = supportPhone(); // 상담센터 — 매니저 전화번호 없을 때 폴백(general.support_phone)

const STATUS_LABEL = { planned: "예정", in_progress: "오는 중", completed: "다녀감", cancelled: "취소" };

// risk_level 을 "위험" 같은 경고어 대신 안심 문구로 번역. 실제 위험 대응(알림·
// 매니저 연락)은 백엔드/보호자/매니저 경로가 담당하고, 어르신 홈은 겁주지 않는다.
const CONDITION = {
  stable:   { text: "오늘도 평안하세요. 잘 지내고 계세요.", color: "var(--green-600)" },
  warning:  { text: "오늘은 컨디션을 한 번 살펴 주세요.",   color: "var(--amber-600)" },
  critical: { text: "담당 선생님이 곧 연락드릴 거예요.",     color: "var(--orange-600)" },
};

// 헤더 헤드라인(1인칭) — 보호자 홈의 "오늘 어머니는 [위험]해요"와 같은 자리·같은
// 크기(18px)를 쓰되 관점만 1인칭으로. 키워드 한 단어만 색으로 강조(보호자 홈 패턴).
const COND_HEAD = {
  stable:   { pre: "오늘도 ",     em: "평안",   post: "하세요",    c: "var(--green-600)"  },
  warning:  { pre: "오늘 ",       em: "컨디션",  post: "을 살펴요",  c: "var(--amber-600)"  },
  critical: { pre: "곧 ",         em: "연락",   post: "드릴게요",   c: "var(--orange-600)" },
};
// 다크 요약카드 ③번째 타일(컨디션) 짧은 라벨.
const COND_TILE = { stable: "평안", warning: "살펴요", critical: "연락 예정" };

// 보호자 홈 다크카드와 동일한 위험도 팔레트(어두운 배경 위 pill/타일 색). 어르신
// 홈은 "위험" 단어를 노출하지 않으므로 키는 상태 의미(stable/warning/critical)로만 쓴다.
const RISK_BG_DARK = { stable: "rgba(143,200,168,0.20)", warning: "rgba(232,188,87,0.20)", critical: "rgba(220,139,130,0.20)" };
const RISK_FG_DARK = { stable: "#9BD0AA", warning: "#E8BC57", critical: "#DC8B82" };
const RISK_TILE_FG = { stable: "#9BD0AA", warning: "#E8BC57", critical: "#DC8B82" };

const WEEKDAY = ["일", "월", "화", "수", "목", "금", "토"];

// "오후 2:00" — 24시제 금지, 어르신 가독성 위해 오전/오후 + 시:분.
function fmtKoreanTime(d) {
  return d.toLocaleTimeString("ko-KR", { hour: "numeric", minute: "2-digit" });
}

// 주격 조사 이/가 — 한글 마지막 글자의 받침 유무로 결정. "선생님"(받침)→이,
// "매니저"(받침 없음)→가. 1인칭 홈 문장의 어색함("선생님가")을 막는다.
function subjectParticle(word) {
  const last = (word || "").charCodeAt((word || "").length - 1);
  if (Number.isNaN(last) || last < 0xac00 || last > 0xd7a3) return "가"; // 한글 아님 → 기본
  return (last - 0xac00) % 28 !== 0 ? "이" : "가";
}

// 자격(qualification)을 일상 호칭으로. 미상이면 "{이름} 매니저".
// 메모리/설계 5.②: 사회복지사→선생님, 요양보호사→요양보호사님 등.
function honorific(mgr) {
  const q = mgr?.qualification || "";
  if (q.includes("요양보호")) return "요양보호사님";
  if (q.includes("간호"))     return "간호사님";
  if (q.includes("사회복지")) return "선생님";
  return "매니저";
}

function ElderEmpty({ title, desc }) {
  // 403(연결된 돌봄 없음)·미배정 등 빈 상태. 빨간 에러 대신 상담센터 전화로 안내.
  return (
    <div className="app" style={{ background: "var(--bg)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <ElderStatusBar />
      <div style={{ padding: "16px 22px 4px" }}>
        <div style={{ fontSize: 21, fontWeight: 800 }}>{title}</div>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 22px" }}>
        <div className="card" style={{ padding: 20, borderRadius: 18, textAlign: "center" }}>
          <div style={{ width: 54, height: 54, borderRadius: 16, margin: "0 auto 14px", background: "var(--green-50)", color: "var(--green-600)", display: "grid", placeItems: "center" }}>
            <span style={{ width: 26, height: 26, display: "inline-block" }}>{Ic.phone}</span>
          </div>
          <div style={{ fontSize: 15, color: "var(--ink-2)", lineHeight: 1.6, marginBottom: 18 }}>{desc}</div>
          <a href={`tel:${CONSULT_TEL}`} className="btn btn-secondary"
             style={{ width: "100%", height: 54, fontSize: 16, fontWeight: 700, textDecoration: "none", display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
            <span style={{ width: 20, height: 20, display: "inline-block" }}>{Ic.phone}</span>
            상담센터 전화 ({CONSULT_TEL})
          </a>
        </div>
      </div>
      <div style={{ marginTop: "auto" }}><ElderTabBar active="home" /></div>
      <div style={{ height: 4, width: 120, background: "var(--ink-1)", borderRadius: 99, margin: "2px auto 6px" }} />
    </div>
  );
}

function ElderStatusBar() {
  return (
    <div aria-hidden style={{ display: "flex", justifyContent: "space-between", padding: "8px 24px 4px", fontSize: 11, fontWeight: 600, color: "var(--ink-1)" }}>
      <span>9:41</span>
      <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
        <span>●●●</span><span style={{ fontSize: 10 }}>5G</span>
        <span style={{ width: 18, height: 9, border: "1px solid var(--ink-1)", borderRadius: 2, position: "relative" }}><span style={{ position: "absolute", inset: 1, background: "var(--ink-1)" }} /></span>
      </div>
    </div>
  );
}

function DashboardElder() {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [needsConnect, setNeedsConnect] = React.useState(false);
  const { user } = useCurrentUser();

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/v1/dashboard/guardian", { credentials: "same-origin" });
        const body = await r.json().catch(() => null);
        if (cancelled) return;
        if (!r.ok) {
          if (r.status === 401)      setErr("login");
          else if (r.status === 403) setNeedsConnect(true); // 연결된 돌봄 없음
          else                       setErr("server");
          return;
        }
        setErr(null);
        setData(body);
      } catch (e) {
        if (!cancelled) setErr("server");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (needsConnect) {
    return <ElderEmpty title="아직 연결된 돌봄이 없어요"
      desc={<>담당자 연결을 도와드릴게요.<br />상담센터로 전화 주세요.</>} />;
  }
  if (err === "login") {
    return (
      <div className="app" style={{ background: "var(--bg)", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: 32, textAlign: "center" }}>
        <div style={{ fontSize: 16, color: "var(--ink-2)", lineHeight: 1.6, marginBottom: 20 }}>다시 로그인이 필요해요.</div>
        <a href="/login" className="btn btn-primary" style={{ height: 54, fontSize: 16, fontWeight: 700, padding: "0 36px", textDecoration: "none", display: "flex", alignItems: "center" }}>로그인</a>
      </div>
    );
  }
  if (err === "server") {
    return <ElderEmpty title="잠시 후 다시 열어 주세요"
      desc={<>화면을 불러오지 못했어요.<br />계속 안 되면 상담센터로 전화 주세요.</>} />;
  }
  if (!data) {
    // 로딩 — 큰 화면 깜빡임 최소화용 스켈레톤(히어로 자리만).
    return (
      <div className="app" style={{ background: "var(--bg)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <ElderStatusBar />
        <div style={{ padding: "14px 24px" }}>
          <div style={{ height: 18, width: 120, background: "var(--bg-deep)", borderRadius: 8 }} />
          <div style={{ height: 30, width: 200, background: "var(--bg-deep)", borderRadius: 8, marginTop: 10 }} />
          <div style={{ height: 180, background: "var(--bg-deep)", borderRadius: 24, marginTop: 18 }} />
        </div>
        <div style={{ marginTop: "auto" }}><ElderTabBar active="home" /></div>
      </div>
    );
  }

  const e = data.elderly;
  const mgr = data.assigned_manager;
  const up = data.upcoming_visit;
  const name = user?.name || e.name;
  const head = COND_HEAD[e.risk_level] || COND_HEAD.stable;
  const pending = !mgr || mgr.proposed; // 담당자 미확정(제안/미배정)

  const now = new Date();

  // 다크 요약카드 윗줄(주인공) — 보호자 홈의 "어머니 요약" 자리에 어르신은
  // "오늘 방문"을 넣는다(자기 자신을 3인칭으로 보는 문제 회피). 상태 분기 동일.
  let heroTime, heroLine, heroPill = null;
  if (up) {
    const who = mgr ? `${mgr.name} ${honorific(mgr)}` : "담당자";
    const inProg = up.status === "in_progress";
    heroTime = fmtKoreanTime(new Date(up.scheduled_at));
    heroLine = `${who}${subjectParticle(who)} ${inProg ? "와 계세요" : "오세요"}`;
    heroPill = { text: inProg ? "오는 중" : "도착 예정", c: inProg ? "warning" : "stable" };
  } else if (mgr && !mgr.proposed) {
    heroTime = "오늘은 방문이 없어요";
    heroLine = `담당 ${mgr.name} ${honorific(mgr)}`;
  } else {
    heroTime = "담당자 배정 중";
    heroLine = "곧 소개해 드릴게요";
  }

  // 타일 2: 다음 예정 방문(오늘 이후 planned 중 가장 이른 건). upcoming + recent 병합.
  const futurePlanned = [...(up ? [up] : []), ...(data.recent_visits || [])]
    .filter((v) => v.status === "planned" && new Date(v.scheduled_at) >= now)
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const nextV = futurePlanned[0] ? new Date(futurePlanned[0].scheduled_at) : null;

  // 다크카드 3-타일 — 보호자 홈(복약/케어/위험)과 같은 레이아웃·크기.
  const tiles = [
    { l: "담당",     v: mgr ? mgr.name : "배정 중",                       s: mgr ? honorific(mgr) : "곧 안내",                       c: "#fff" },
    { l: "다음 방문", v: nextV ? `${nextV.getMonth() + 1}/${nextV.getDate()}` : "-", s: nextV ? `${WEEKDAY[nextV.getDay()]}요일` : "예정 없음", c: "#fff" },
    { l: "컨디션",    v: COND_TILE[e.risk_level] || "평안",                s: "오늘",                                                c: RISK_TILE_FG[e.risk_level] || "#9BD0AA" },
  ];

  const recent = (data.recent_visits || []).slice(0, 3);
  const callTel = mgr?.phone || CONSULT_TEL;

  return (
    <div className="app" style={{ background: "var(--bg)", overflow: "auto", display: "flex", flexDirection: "column" }}>
      <ElderStatusBar />

      {/* ① 인사 + 1인칭 헤드라인 + 우측 도움말/알림 — 보호자 홈 헤더와 동일 구조 */}
      <div style={{ padding: "14px 22px 6px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, color: "var(--ink-3)" }}>안녕하세요, {name} 님</div>
          <div style={{ fontSize: 20, fontWeight: 800 }}>{head.pre}<span style={{ color: head.c }}>{head.em}</span>{head.post}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <a href="/help" title="도움말"
             style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 9px", borderRadius: 8, border: "1px solid var(--border-soft)", background: "var(--surface)", color: "var(--ink-1)", fontSize: 15, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" }}>
            <span style={{ display: "inline-flex", width: 13, height: 13, color: "var(--orange-600)" }}>{Ic.help}</span>
            도움말
          </a>
          <a href="/m/notify" style={{ position: "relative", color: "var(--ink-1)", textDecoration: "none" }}
             aria-label={`알림 ${data.unread_notifications || 0}건`}>
            <span style={{ width: 22, height: 22, display: "inline-block" }}>{Ic.bell}</span>
            {(data.unread_notifications || 0) > 0 && (
              <span style={{ position: "absolute", top: -2, right: -2, minWidth: 14, height: 14, padding: "0 3px", borderRadius: 99, background: "var(--orange-500)", color: "#fff", fontSize: 10, fontWeight: 800, display: "grid", placeItems: "center" }}>
                {data.unread_notifications > 99 ? "99+" : data.unread_notifications}
              </span>
            )}
          </a>
        </div>
      </div>

      {/* ② 다크 요약 카드 — 보호자 홈과 동일 chrome(그라데이션/원형 장식/3타일).
          주인공만 "오늘 방문"으로(1인칭). */}
      <div style={{ margin: "14px 22px 0", padding: 18, background: "linear-gradient(135deg, #2A241F, #3E3329)", color: "#F5EBD8", borderRadius: 18, position: "relative", overflow: "hidden", flexShrink: 0 }}>
        <div style={{ position: "absolute", top: -40, right: -30, width: 140, height: 140, borderRadius: "50%", background: "rgba(232,130,91,0.15)" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="avatar lg" style={{ background: "linear-gradient(135deg, #EFB287, #E8825B)" }}>
            <span style={{ width: 22, height: 22, display: "grid", placeItems: "center", fontSize: 22 }}>{pending ? Ic.clock : Ic.schedule}</span>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, opacity: .7 }}>오늘 방문</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#fff" }}>{heroTime}</div>
            <div style={{ fontSize: 12, color: "#A89A7C", marginTop: 2 }}>{heroLine}</div>
          </div>
          {heroPill && (
            <span style={{ marginLeft: "auto", padding: "4px 10px", background: RISK_BG_DARK[heroPill.c], color: RISK_FG_DARK[heroPill.c], borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>{heroPill.text}</span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          {tiles.map((t, i) => (
            <div key={i} style={{ flex: 1, padding: 10, background: "rgba(255,255,255,0.06)", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ fontSize: 11, opacity: .65 }}>{t.l}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: t.c, marginTop: 2, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>{t.v}</div>
              <div style={{ fontSize: 10, color: "#A89A7C", marginTop: 1, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>{t.s}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 응급SOS 빠른 진입 — 어르신 홈에도 크게 노출(큰 글씨·큰 터치영역). */}
      <div style={{ padding: "18px 22px 0" }}>
        <a href="/m/sos" aria-label="응급SOS 신청"
           style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 18px", borderRadius: 16, textDecoration: "none",
                    background: "linear-gradient(135deg, var(--rose-500), var(--rose-600))", color: "#fff", boxShadow: "0 12px 26px -10px rgba(201,89,79,0.42)" }}>
          <span style={{ width: 44, height: 44, borderRadius: 13, background: "rgba(255,255,255,0.18)", display: "grid", placeItems: "center", flex: "0 0 auto" }}>
            <span style={{ width: 22, height: 22, display: "grid", placeItems: "center", fontSize: 22 }}>{Ic.alert}</span>
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 800 }}>응급SOS 신청</div>
            <div style={{ fontSize: 12.5, color: "var(--rose-50)", marginTop: 2, opacity: .92 }}>위급할 때 눌러 주세요. 바로 도와드려요.</div>
          </div>
          <span aria-hidden style={{ fontSize: 22, opacity: .85 }}>›</span>
        </a>
      </div>

      {/* ③ 담당 매니저 카드 — 보호자 홈 "오늘 방문 매니저" 카드와 동일. 단 전화는
          항상 활성(번호 없으면 상담센터 폴백) — 어르신은 비활성 버튼을 이해 못 함. */}
      {!pending && mgr && (
      <div style={{ padding: "18px 22px 0" }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>담당 매니저</span>
          <a href="/m/timeline" style={{ fontSize: 13, color: "var(--orange-600)", fontWeight: 600, textDecoration: "none" }}>전체 →</a>
        </div>
        <div className="card" style={{ padding: 14, borderRadius: 14, display: "flex", alignItems: "center", gap: 12 }}>
          <AvatarImg className="avatar lg green" url={mgr.avatar_url} name={mgr.name}/>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{mgr.name} {honorific(mgr)}</div>
            {mgr.organization_name && <div style={{ fontSize: 12, color: "var(--ink-3)" }}>{mgr.organization_name}</div>}
            <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
              {up ? (
                <span className="badge badge-orange" style={{ fontSize: 13 }}>{fmtKoreanTime(new Date(up.scheduled_at))} {up.status === "in_progress" ? "오는 중" : "도착 예정"}</span>
              ) : (
                <span className="badge badge-gray" style={{ fontSize: 13 }}>오늘 예정 방문 없음</span>
              )}
            </div>
          </div>
          <a href={`tel:${callTel}`} aria-label={`${mgr.name} ${honorific(mgr)}에게 전화`}
             style={{ width: 34, height: 34, borderRadius: 99, background: "var(--green-500)", color: "#fff", display: "grid", placeItems: "center", textDecoration: "none", flex: "0 0 auto" }}>
            <span style={{ width: 18, height: 18, display: "inline-block" }}>{Ic.phone}</span>
          </a>
        </div>
      </div>
      )}

      {/* ④ 방문 일정 — 보호자 홈 "최근 방문" 카드와 동일 스타일. */}
      <div style={{ padding: "18px 22px 0" }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>방문 일정</span>
          <a href="/m/timeline" style={{ fontSize: 13, color: "var(--orange-600)", fontWeight: 600, textDecoration: "none" }}>전체 →</a>
        </div>
        <div className="card" style={{ padding: 14, borderRadius: 14 }}>
          {recent.length === 0 && <div className="muted" style={{ fontSize: 14 }}>아직 방문 기록이 없어요.</div>}
          {recent.map((v, i) => {
            const dt = new Date(v.scheduled_at);
            const title = v.qualification ? honorific({ qualification: v.qualification }) : "님";
            return (
              <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderTop: i > 0 ? "1px solid var(--border-soft)" : "none" }}>
                <div style={{ textAlign: "center", minWidth: 34 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-3)" }}>{WEEKDAY[dt.getDay()]}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "var(--ink-1)", lineHeight: 1 }}>{dt.getDate()}</div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{fmtKoreanTime(dt)} · {v.manager_name} {title}</div>
                </div>
                <span className={"badge " + (v.status === "completed" ? "badge-green" : v.status === "in_progress" ? "badge-orange" : "badge-gray")} style={{ fontSize: 13 }}>{STATUS_LABEL[v.status]}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ⑤ 도움 — 도움말 + 상담센터(작은 보조 링크). 헤더 도움말 pill과 짝. */}
      <div style={{ padding: "18px 22px 0" }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>도움이 필요하세요?</div>
        <div className="card" style={{ padding: 14, borderRadius: 14 }}>
          <a href="/help" style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", textDecoration: "none", color: "var(--ink-1)" }}>
            <span style={{ width: 18, height: 18, color: "var(--orange-600)", display: "inline-block" }}>{Ic.help}</span>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>도움말 보기</span>
            <span aria-hidden style={{ color: "var(--ink-3)", fontSize: 18 }}>›</span>
          </a>
          <a href={`tel:${CONSULT_TEL}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderTop: "1px solid var(--border-soft)", textDecoration: "none", color: "var(--ink-1)" }}>
            <span style={{ width: 18, height: 18, color: "var(--green-600)", display: "inline-block" }}>{Ic.phone}</span>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>상담센터 전화</span>
            <span style={{ fontSize: 12, color: "var(--ink-3)" }}>{CONSULT_TEL}</span>
          </a>
          <a href="/m/elder-manual" style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderTop: "1px solid var(--border-soft)", textDecoration: "none", color: "var(--ink-1)" }}>
            <span style={{ width: 18, height: 18, color: "var(--sky-600)", display: "inline-block" }}>{Ic.book || Ic.help}</span>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>사용자 매뉴얼</span>
            <span aria-hidden style={{ color: "var(--ink-3)", fontSize: 18 }}>›</span>
          </a>
        </div>
      </div>

      <div style={{ height: 14 }} />
      <div style={{ marginTop: "auto" }}><ElderTabBar active="home" /></div>
      <div style={{ height: 4, width: 120, background: "var(--ink-1)", borderRadius: 99, margin: "2px auto 6px" }} />
    </div>
  );
}

export { DashboardElder };
